import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { addUsernameToIndex, searchPubKeysFromLocal } from '@/services/client.service'
import { loadNostrUser } from '@nostr/gadgets/metadata'
import { HugeiconsIcon } from '@hugeicons/react'
import { UserGroupIcon } from '@hugeicons/core-free-icons'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SimpleUserAvatar } from './UserAvatar'
import { getErrorMessage } from '@/lib/error'

function JustUsername({ pubkey }: { pubkey: string }) {
  const [name, setName] = useState('')
  useEffect(() => {
    loadNostrUser(pubkey)
      .then((p) => {
        addUsernameToIndex(p)
        setName(p.shortName)
      })
      .catch((error) => console.warn('failed to load profile:', getErrorMessage(error)))
  }, [pubkey])
  return <>{name || pubkey.slice(0, 8)}</>
}

function loadAuthors(key: string): string[] {
  try {
    const raw = sessionStorage.getItem(key)
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    return []
  }
}

function saveAuthors(key: string, authors: string[]) {
  sessionStorage.setItem(key, JSON.stringify(authors))
}

function loadKnown(key: string): string[] {
  try {
    const raw = sessionStorage.getItem(`${key}:known`)
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    return []
  }
}

function saveKnown(key: string, authors: string[]) {
  sessionStorage.setItem(`${key}:known`, JSON.stringify(authors))
}

export default function ProfileCombobox({
  storageKey,
  onChange
}: {
  storageKey: string
  onChange?: (pubkeys: string[]) => void
}) {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<string[]>(() => loadAuthors(storageKey))
  const [knownPubkeys, setKnownPubkeys] = useState<string[]>(() => {
    const saved = loadKnown(storageKey)
    const current = loadAuthors(storageKey)
    // merge saved + current, dedupe
    return Array.from(new Set([...saved, ...current]))
  })
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    saveAuthors(storageKey, selected)
    onChange?.(selected)
  }, [storageKey, selected, onChange])

  useEffect(() => {
    saveKnown(storageKey, knownPubkeys)
  }, [storageKey, knownPubkeys])

  useEffect(() => {
    if (!search.trim()) {
      setResults(knownPubkeys)
      return
    }
    const searchResults = searchPubKeysFromLocal(search, 20)
    const matched = knownPubkeys.filter((pk) => searchResults.includes(pk))
    const rest = searchResults.filter((pk) => !knownPubkeys.includes(pk))
    setResults([...new Set([...matched, ...rest])])
  }, [search, knownPubkeys])

  const togglePubkey = useCallback((pubkey: string) => {
    setSelected((prev) => {
      if (prev.includes(pubkey)) return prev.filter((p) => p !== pubkey)
      return [...prev, pubkey]
    })
    setKnownPubkeys((prev) => {
      if (prev.includes(pubkey)) return prev
      return [...prev, pubkey]
    })
    setSearch('')
    inputRef.current?.focus()
  }, [])

  return (
    <div className="flex flex-wrap items-center gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="titlebar-icon"
            className={cn(
              'relative gap-1 px-3 w-auto shrink focus:text-foreground',
              selected.length === 0 && 'text-muted-foreground'
            )}
          >
            <HugeiconsIcon icon={UserGroupIcon} size={16} />
            <span className="hidden lg:block ml-1">
              {selected.length > 0
                ? t('Authors: {{count}}', { count: selected.length })
                : t('Authors')}
            </span>
            {selected.length > 0 && (
              <div className="absolute size-2 rounded-full bg-primary left-7 top-2 ring-2 ring-background" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <div className="flex items-center border-b px-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="shrink-0 opacity-50 mr-2"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.3-4.3"></path>
            </svg>
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('Search profiles...')}
              className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
              autoFocus
            />
          </div>
          <ScrollArea className="max-h-64">
            {results.length === 0 && search.trim() && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {t('No profiles found')}
              </div>
            )}
            {results.map((pubkey) => {
              const isSelected = selected.includes(pubkey)
              return (
                <div
                  key={pubkey}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-muted',
                    isSelected && 'bg-muted/50'
                  )}
                  onClick={() => togglePubkey(pubkey)}
                >
                  <SimpleUserAvatar user={pubkey} className="size-7 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      <JustUsername pubkey={pubkey} />
                    </div>
                  </div>
                  {isSelected && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="shrink-0 text-primary"
                    >
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  )}
                </div>
              )
            })}
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </div>
  )
}
